import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _AbIterable, _AbIterableIterator;
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

/**
 * Wrapper class for Iterable that provides helper functions.
 *
 * AbIterable's helper functions return iterables that are repeatable unless the underlying iterable is not repeatable.
 */
export class AbIterable {
  constructor(iterable) {
    _defineProperty(this, "_iterable", void 0);
    /**
     * Supports the Iterable interface.
     */
    _defineProperty(this, Symbol.iterator, () => {
      return this._iterable[Symbol.iterator]();
    });
    /**
     * Helper function to expose iterator constructor.
     */
    _defineProperty(this, "iterator", () => {
      return this[Symbol.iterator]();
    });
    /**
     * Returns a new iterable with the map function applied to each value from this iterable.
     * @param mapFunction
     */
    _defineProperty(this, "map", mapFunction => {
      return AbIterable.map(this, mapFunction);
    });
    /**
     * returns a new iterable representing the concatenation of all of the iterables generated by the flatMap function.
     * @param flatMapFunction
     */
    _defineProperty(this, "flatMap", flatMapFunction => {
      return AbIterable.flatMap(this, flatMapFunction);
    });
    /**
     * Returns a new iterable which only yields values that pass the filter function.
     * Restricts type of return value.
     * @param filterFunction Type guard for return type.
     */
    _defineProperty(this, "filterType", filterFunction => {
      return AbIterable.filterType(this, filterFunction);
    });
    /**
     * Returns a new iterable which only yields values that pass the filter function.
     * Does not restrict type of return value.
     * @param filterFunction
     */
    _defineProperty(this, "filter", filterFunction => {
      return AbIterable.filter(this, filterFunction);
    });
    _defineProperty(this, "find", predicate => {
      return AbIterable.find(this, predicate);
    });
    /**
     * Returns whether at least one element in the iterable satisfies the predicate.
     * @param predicate
     */
    _defineProperty(this, "any", predicate => {
      return AbIterable.any(this, predicate);
    });
    /**
     * Returns whether every element in the iterable satisfies the predicate.
     * @param predicate
     */
    _defineProperty(this, "every", predicate => {
      return AbIterable.every(this, predicate);
    });
    _defineProperty(this, "reduce", (reducer, initialValue) => {
      return AbIterable.reduce(this, reducer, initialValue);
    });
    /**
     * Utility function to convert this to an array.
     */
    _defineProperty(this, "toArray", () => {
      return [...this];
    });
    /**
     * Utility function to resolve the iterable by calling a consumer on each of its elements.
     */
    _defineProperty(this, "forEach", consumer => {
      return AbIterable.forEach(this, consumer);
    });
    _defineProperty(this, "count", () => {
      return AbIterable.count(this);
    });
    /**
     * Utility function to convert this to a map.
     * @param keyFunction generates a map key from the value T.
     * @param valFunction generates a map value from the value T.
     * @param mergeFunction optional function to merge two values if a value already exists for a key and a new value is added.
     */
    _defineProperty(this, "toMap", (keyFunction, valFunction, mergeFunction) => {
      return AbIterable.toMap(this, keyFunction, valFunction, mergeFunction);
    });
    /**
     * Utility function to convert this to a set.
     */
    _defineProperty(this, "toSet", () => {
      return AbIterable.toSet(this);
    });
    /**
     * Utilty function to join the contents of the iterable to a string.
     * @param delimiter optional delimiter to separate the elements of the string
     */
    _defineProperty(this, "join", delimiter => {
      return AbIterable.join(this, delimiter);
    });
    this._iterable = iterable;
  }
  /**
   * Returns the first n values of the iterable as an array.
   * @param n
   */
  first(n) {
    if (n === undefined) {
      return AbIterable.first(this, n);
    }
    return AbIterable.first(this, n);
  }
  /**
   * Returns either the first n elements of an iterable or all the elements of an iterable, whichever is shorter.
   * @param iterable
   * @param n
   */
  static first(iterable, n) {
    const newN = n === undefined ? 1 : n;
    const values = [];
    let i = 0;
    for (let v of iterable) {
      if (i >= newN) {
        break;
      }
      values.push(v);
      i++;
    }
    if (n === undefined) {
      return values.length > 0 ? values[0] : undefined;
    }
    return values;
  }

  /**
   * Returns whether at least one element in the iterable satisfies the predicate.
   * @param iterable
   * @param predicate
   */
}

/**
 * Wrapper class for when mapping iterable iterators is needed. Mostly used for the keys/values of maps and sets.
 */
_AbIterable = AbIterable;
/**
 * Allows construction of AbIterable from any Iterable.
 * @param iterable
 */
_defineProperty(AbIterable, "from", iterable => {
  return new _AbIterable(iterable);
});
_defineProperty(AbIterable, "fromNodeList", nodeList => {
  return _AbIterable.from({
    [Symbol.iterator]: function* () {
      for (let i = 0; i < nodeList.length; i++) {
        yield nodeList.item(i);
      }
    }
  });
});
_defineProperty(AbIterable, "EMPTY", _AbIterable.from([]));
/**
 * Returns an empty AbIterable.
 */
_defineProperty(AbIterable, "empty", () => {
  return _AbIterable.EMPTY;
});
/**
 * Returns a new iterable with mapFunction applied to each value from the input iterable.
 * @param iterable
 * @param mapFunction
 */
_defineProperty(AbIterable, "map", (iterable, mapFunction) => {
  return _AbIterable.from({
    [Symbol.iterator]: function* () {
      let i = 0;
      for (const v of iterable) {
        yield mapFunction(v, i);
        i++;
      }
    }
  });
});
/**
 * Returns a new iterable representing the concatenation of all of the results of flatMapFunction applied to the values of the input iterable.
 * @param iterable
 * @param flatMapFunction
 */
_defineProperty(AbIterable, "flatMap", (iterable, flatMapFunction) => {
  return _AbIterable.from({
    [Symbol.iterator]: function* () {
      let i = 0;
      for (const v of iterable) {
        let currentSubIterator = flatMapFunction(v, i);
        if (currentSubIterator) {
          for (const v2 of currentSubIterator) {
            yield v2;
          }
        }
        i++;
      }
    }
  });
});
/**
 * Joins N iterables with all the items of iterables[0] happening first, followed by the items of iterables[1]...iterables[n].
 * @param iterables
 */
_defineProperty(AbIterable, "concat", (...iterables) => {
  return _AbIterable.from({
    [Symbol.iterator]: function* () {
      for (const iter of iterables) {
        for (const v of iter) {
          yield v;
        }
      }
    }
  });
});
/**
 * Returns a new iterable that only produces values that are generated by the input iterable and pass the filter function.
 * Does not restrict type of return value.
 * @param iterable
 * @param filterFunction
 */
_defineProperty(AbIterable, "filter", (iterable, filterFunction) => {
  const typeFilter = (v, i) => {
    return filterFunction(v, i);
  };
  return _AbIterable.filterType(iterable, typeFilter);
});
/**
 * Returns a new iterable that only produces values that are generated by the input iterable and pass the filter function.
 * Restricts type of return value.
 * @param iterable
 * @param filterFunction Type guard for output.
 */
_defineProperty(AbIterable, "filterType", (iterable, filterFunction) => {
  return _AbIterable.from({
    [Symbol.iterator]: function* () {
      let i = 0;
      for (let v of iterable) {
        if (filterFunction(v, i)) {
          yield v;
        }
        i++;
      }
    }
  });
});
/**
 * Return an iterable of numbers incrementing from start (inclusive) to end (exclusive).
 * If end isn't provided, the range [0, start) is returned.
 * Will error if increment is 0.
 * @param {number} start
 * @param {number} end
 * @param {number} increment (default = 1)
 */
_defineProperty(AbIterable, "range", (start, end, increment = 1) => {
  if (increment === 0) {
    throw new Error("Cannot create range with increment = 0");
  }
  return _AbIterable.from({
    [Symbol.iterator]: function* () {
      if (end === undefined) {
        end = start;
        start = 0;
      }
      if (increment > 0 && start >= end || increment < 0 && start <= end) {
        return undefined;
      }
      for (let i = start; increment > 0 ? i < end : i > end; i += increment) {
        yield i;
      }
    }
  });
});
/**
 * Finds the first element that satisfies the predicate and returns it. If none are found, undefined.
 */
_defineProperty(AbIterable, "find", (iterable, predicate) => {
  for (let v of iterable) {
    if (predicate(v)) return v;
  }
  return undefined;
});
_defineProperty(AbIterable, "any", (iterable, predicate) => {
  for (let v of iterable) {
    if (predicate(v)) {
      return true;
    }
  }
  return false;
});
/**
 * Returns whether at every element in the iterable satisfies the predicate.
 * @param iterable
 * @param predicate
 */
_defineProperty(AbIterable, "every", (iterable, predicate) => {
  for (let v of iterable) {
    if (!predicate(v)) {
      return false;
    }
  }
  return true;
});
/**
 * Calls the consumer on each element of the iterable
 * @param iterable
 * @param consumer
 */
_defineProperty(AbIterable, "forEach", (iterable, consumer) => {
  for (const v of iterable) {
    consumer(v);
  }
  return _AbIterable.from(iterable);
});
_defineProperty(AbIterable, "count", iterable => _AbIterable.reduce(iterable, i => i + 1, 0));
/**
 * Converts the iterable to a map
 * @param iterable
 * @param keyFunction function to convert an iterable value to a map key
 * @param valueFunction function to convert an interable value to a map value
 * @param mergeFunction optional function to be used to deduplicate or merge values if two values have the same key.
 */
_defineProperty(AbIterable, "toMap", (iterable, keyFunction, valueFunction, mergeFunction) => {
  //todo sam convert to AbKeyMap once input keyFunction and valueFunction are combined
  const result = new Map();
  for (const v of iterable) {
    const key = keyFunction(v);
    const newVal = valueFunction(v);
    const existingVal = result.get(key);
    if (existingVal !== undefined && mergeFunction) {
      const merged = mergeFunction(existingVal, newVal);
      result.set(key, merged);
    } else {
      result.set(key, newVal);
    }
  }
  return result;
});
/**
 * Converts the iterable to a set
 * @param iterable
 */
_defineProperty(AbIterable, "toSet", iterable => {
  const result = new Set();
  for (const v of iterable) {
    result.add(v);
  }
  return result;
});
/**
 * Joins the iterable to a string
 * @param iterable
 * @param delimiter optional delimiter to separate the string values
 */
_defineProperty(AbIterable, "join", (iterable, delimiter) => {
  let result = "";
  let delimiterVal = delimiter ?? "";
  let isFirst = true;
  for (const v of iterable) {
    if (isFirst) {
      result = result + v;
      isFirst = false;
    } else {
      result = result + delimiterVal + v;
    }
  }
  return result;
});
_defineProperty(AbIterable, "reduce", (iterable, reducer, initialValue) => {
  let cur = initialValue;
  let i = 0;
  for (let v of iterable) {
    cur = reducer(cur, v, i);
    i++;
  }
  return cur;
});
export class AbIterableIterator {
  constructor(_iterableIterator) {
    _defineProperty(this, "next", () => this._iterableIterator.next());
    _defineProperty(this, "map", mapFunction => {
      const indexObj = {
        index: 0
      };
      return AbIterableIterator.from({
        next: () => {
          const retVal = this.next();
          if (retVal.done === true) {
            return {
              done: true,
              value: undefined
            };
          }
          return {
            done: retVal.done,
            value: mapFunction(retVal.value, ++indexObj.index)
          };
        },
        [Symbol.iterator]: () => {
          return this.map(mapFunction);
        }
      });
    });
    _defineProperty(this, Symbol.iterator, () => {
      return AbIterableIterator.from(this._iterableIterator[Symbol.iterator]());
    });
    this._iterableIterator = _iterableIterator;
  }
}
_AbIterableIterator = AbIterableIterator;
_defineProperty(AbIterableIterator, "from", iterableIterator => new _AbIterableIterator(iterableIterator));
export class AbIterator {}
_defineProperty(AbIterator, "forEach", (iterator, f) => {
  let index = 0;
  while (true) {
    const retVal = iterator.next();
    if (retVal.done === true) return;
    f(retVal.value, ++index);
  }
});
//# sourceMappingURL=AbIterable.js.map
